import { BaseApiClient } from '../base-client'
import {
  API_ROUTES,
  ApiResponse,
  AccountCredentialIntegrationType,
  interpolateRoute,
} from '@super/shared-types'
import { withErrorHandling } from '../../utils'

export interface BuildingLinkMetadata {
  defaultUnit: string
}

/**
 * BuildingLink Integration API Client
 * Handles all BuildingLink-specific API operations
 */
export class BuildingLinkClient extends BaseApiClient {
  /**
   * Create account credentials for BuildingLink
   */
  async createCredentials(
    accountIdentifier: string,
    password: string,
    metadata?: BuildingLinkMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.ACCOUNT_CREDENTIALS.CREATE,
          { integrationType: AccountCredentialIntegrationType.BUILDING_LINK },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'POST',
          body: JSON.stringify({ accountIdentifier, password, metadata }),
        })
      },
      {
        errorContext: 'Error creating BuildingLink account credentials',
      },
    )
  }

  /**
   * [ADMIN] Create account credentials for BuildingLink for a specific company
   */
  async createAdminCredentials(
    companyId: string,
    accountIdentifier: string,
    password: string,
    metadata?: BuildingLinkMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.ACCOUNT_CREDENTIALS.CREATE,
          { integrationType: AccountCredentialIntegrationType.BUILDING_LINK },
        )
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'POST',
            body: JSON.stringify({ accountIdentifier, password, metadata }),
          },
        )
      },
      {
        errorContext: 'Error creating admin BuildingLink account credentials',
      },
    )
  }

  /**
   * Update account credentials for BuildingLink
   */
  async updateCredentials(
    accountIdentifier: string,
    password: string,
    metadata?: BuildingLinkMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.ACCOUNT_CREDENTIALS.UPDATE,
          { integrationType: AccountCredentialIntegrationType.BUILDING_LINK },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'PUT',
          body: JSON.stringify({ accountIdentifier, password, metadata }),
        })
      },
      {
        errorContext: 'Error updating BuildingLink account credentials',
      },
    )
  }

  /**
   * [ADMIN] Update account credentials for BuildingLink for a specific company
   */
  async updateAdminCredentials(
    companyId: string,
    accountIdentifier: string,
    password: string,
    metadata?: BuildingLinkMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.ACCOUNT_CREDENTIALS.UPDATE,
          { integrationType: AccountCredentialIntegrationType.BUILDING_LINK },
        )
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'PUT',
            body: JSON.stringify({ accountIdentifier, password, metadata }),
          },
        )
      },
      {
        errorContext: 'Error updating admin BuildingLink account credentials',
      },
    )
  }

  /**
   * Disconnect BuildingLink integration
   */
  async disconnect(): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.ACCOUNT_CREDENTIALS.DELETE,
          { integrationType: AccountCredentialIntegrationType.BUILDING_LINK },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'DELETE',
        })
      },
      {
        errorContext: 'Error disconnecting BuildingLink integration',
      },
    )
  }

  /**
   * [ADMIN] Disconnect BuildingLink integration for a specific company
   */
  async disconnectAdmin(companyId: string): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.ADMIN.INTEGRATIONS.ACCOUNT_CREDENTIALS.DELETE,
      { integrationType: AccountCredentialIntegrationType.BUILDING_LINK },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'DELETE',
          },
        )
      },
      {
        errorContext: 'Error disconnecting admin BuildingLink integration',
      },
    )
  }
}

// Export singleton instance
export const buildingLinkClient = new BuildingLinkClient()
